import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Mail, Lock, ArrowRight, Building2 } from 'lucide-react';
import { PasswordSetupPrompt } from '../components/PasswordSetupPrompt';
import { authErrorMessage } from '../lib/auth-errors';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const inputClass = 'w-full min-w-0 px-4 py-3 border-2 border-navy/20 rounded-xl focus:border-navy focus:outline-none transition-colors';
const submitClass = 'w-full bg-navy text-cream py-3 rounded-xl font-semibold hover:bg-navy-dark transition-colors flex items-center justify-center gap-2 disabled:opacity-50';

export default function AdvertiserLogin() {
  const navigate = useNavigate();
  const [step, setStep] = useState('email');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const checkEmail = async (event) => {
    event.preventDefault(); setError(''); setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/check-email`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { setError(authErrorMessage(data.detail, 'Email not found.')); return; }
      setEmail(email.trim());
      setCompanyName(data.company_name || '');
      setStep(data.has_password ? 'login' : 'setup');
    } catch { setError('Connection error. Please try again.'); }
    finally { setLoading(false); }
  };

  const login = async (event) => {
    event.preventDefault(); setError(''); setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/login`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        credentials: 'include', body: JSON.stringify({ email, password }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { setError(authErrorMessage(data.detail, 'Login failed.')); return; }
      if (!data.token || !data.advertiser?.id) {
        setError('Sign-in could not be completed. Please try again shortly.'); return;
      }
      localStorage.setItem('advecti_token', data.token);
      localStorage.setItem('advecti_advertiser', JSON.stringify(data.advertiser));
      navigate('/dashboard', { replace: true });
    } catch { setError('Connection error. Please try again.'); }
    finally { setLoading(false); }
  };

  const changeEmail = () => { setStep('email'); setError(''); setPassword(''); };

  return <div className="bg-cream-light min-h-screen" data-testid="advertiser-login-page">
    <section className="hero-gradient py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <h1 className="text-4xl md:text-5xl font-bold text-navy mb-4" data-testid="advertiser-login-title">Advertiser Login</h1>
        <p className="text-lg text-navy/70">Access your campaign dashboard</p>
      </div>
    </section>
    <section className="py-12">
      <div className="max-w-md mx-auto px-4">
        <div className="bg-white rounded-2xl shadow-lg p-6 sm:p-8">
          <div className="flex items-center justify-center gap-2 mb-8" aria-label={`Step ${step === 'email' ? 1 : 2} of 2`} data-testid="login-step-indicator">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${step === 'email' ? 'bg-navy text-cream' : 'bg-navy/20 text-navy'}`}>1</div>
            <div className={`w-8 h-0.5 ${step !== 'email' ? 'bg-navy' : 'bg-navy/20'}`} />
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${step !== 'email' ? 'bg-navy text-cream' : 'bg-navy/20 text-navy'}`}>2</div>
          </div>
          {companyName && step !== 'email' && <div className="text-center mb-6">
            <div className="inline-flex max-w-full items-center gap-2 bg-cream px-4 py-2 rounded-full">
              <Building2 size={16} className="text-navy shrink-0" /><span className="text-navy font-medium text-sm break-words min-w-0" data-testid="login-company-name">{companyName}</span>
            </div>
          </div>}
          {step === 'email' && <form onSubmit={checkEmail} data-testid="email-step-form">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-navy/10 rounded-full flex items-center justify-center mx-auto mb-4"><Mail className="text-navy" size={32} /></div>
              <h2 className="text-base md:text-lg font-bold text-navy">Enter Your Email</h2>
              <p className="text-navy/60 text-sm mt-1">Use the email from your campaign submission</p>
            </div>
            <input type="email" name="email" autoComplete="username" aria-label="Email" value={email} onChange={(e) => setEmail(e.target.value)} required className={`${inputClass} mb-4`} placeholder="your@email.com" data-testid="login-email-input" />
            {error && <p role="alert" className="text-red-600 text-sm mb-4" data-testid="login-error">{error}</p>}
            <button type="submit" disabled={loading} className={submitClass} data-testid="email-submit-btn">{loading ? 'Checking…' : 'Continue'}{!loading && <ArrowRight size={18} />}</button>
          </form>}
          {step === 'login' && <form onSubmit={login} data-testid="login-form">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-navy/10 rounded-full flex items-center justify-center mx-auto mb-4"><Lock className="text-navy" size={32} /></div>
              <h2 className="text-base md:text-lg font-bold text-navy">Enter Your Password</h2>
              <p className="text-navy/60 text-sm mt-1 break-all" data-testid="login-selected-email">{email}</p>
            </div>
            <input type="email" name="username" autoComplete="username" value={email} readOnly hidden data-testid="login-username" />
            <input type="password" name="password" autoComplete="current-password" aria-label="Password" value={password} onChange={(e) => setPassword(e.target.value)} required className={`${inputClass} mb-4`} placeholder="Enter your password" data-testid="login-password-input" />
            {error && <p role="alert" className="text-red-600 text-sm mb-4" data-testid="login-error">{error}</p>}
            <button type="submit" disabled={loading} className={submitClass} data-testid="login-submit-btn">{loading ? 'Signing in…' : 'Sign in'}{!loading && <ArrowRight size={18} />}</button>
            <div className="flex flex-wrap items-center justify-between mt-3 gap-3">
              <button type="button" disabled={loading} onClick={changeEmail} className="text-navy/60 text-sm hover:text-navy transition-colors" data-testid="login-change-email">Use a different email</button>
              <Link to="/forgot-password" className="text-navy/60 text-sm hover:text-navy underline transition-colors" data-testid="forgot-password-link">Forgot password?</Link>
            </div>
          </form>}
          {step === 'setup' && <PasswordSetupPrompt email={email} onBack={changeEmail} />}
        </div>
      </div>
    </section>
  </div>;
}